import {
  CloseOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  PushpinFilled,
  PushpinOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  ConfigProvider,
  Drawer,
  Input,
  InputRef,
  Typography,
} from 'antd'
import { DrilldownDataContext, useDrilldownPanelData } from 'features/drilldown'
import { DRILLDOWN_PANEL_CONFIGS } from 'features/drilldown/components/panelConfigs/PanelConfigs'
import {
  BUTTON_GROUP_SPECS,
  DRILLDOWN_PANELS_TO_GROUPING_TYPES,
} from 'features/drilldown/components/panelConfigs/PanelGroupConfigs'
import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useResizeDetector } from 'react-resize-detector'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { DrilldownPanelType, GenericPanelData } from 'types'

const { Text } = Typography

const DEFAULT_CARD_PADDING = 24
const DEFAULT_ICON_COLOR = 'white'

export type DrilldownPanelProps = {
  panelType: DrilldownPanelType
  onClose?: () => void
  icon?: ReactNode
  configPanel?: ReactNode
  panelPadding?: number
}

const CARD_STYLE: React.CSSProperties = {
  width: '100%',
}

interface DrilldownPanelTitleProps {
  panelType: DrilldownPanelType
  icon?: ReactNode
}

const HeaderContainer = styled.div`
  justify-content: space-between;
  display: flex;
  flex-direction: row;
  padding-right: 0.1rem;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ConfigDrawerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transition: all 0.3s;
  overflow: hidden;
  height: 100%;
`

const DrilldownPanelTitle: FC<DrilldownPanelTitleProps> = ({
  icon,
  panelType,
}) => {
  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)
  const { panelData, setPanelData } = useDrilldownPanelData<GenericPanelData>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const inputFieldRef = useRef<InputRef>(null)

  const { isPinned, title } = panelData

  const iconColor = useMemo(() => {
    if (!panelType) return DEFAULT_ICON_COLOR

    const groupType = DRILLDOWN_PANELS_TO_GROUPING_TYPES[panelType]
    if (!groupType) return DEFAULT_ICON_COLOR

    const buttonGroupSpec = BUTTON_GROUP_SPECS[groupType]
    if (!buttonGroupSpec?.icon) return DEFAULT_ICON_COLOR

    const iconElement = buttonGroupSpec.icon as React.ReactElement
    return iconElement.props?.style?.color ?? DEFAULT_ICON_COLOR
  }, [panelType])

  const toggleIsPinned = useCallback(() => {
    setPanelData((prev: GenericPanelData) => {
      return {
        ...prev,
        isPinned: !prev.isPinned,
      }
    })
  }, [setPanelData])

  const setTitle = useCallback(
    (newTitle: string) => {
      setPanelData((prev: GenericPanelData) => ({ ...prev, title: newTitle }))
    },
    [setPanelData]
  )

  useEffect(() => {
    if (isEditing) {
      inputFieldRef.current?.focus()
    } else {
      inputFieldRef.current?.blur()
    }
  }, [isEditing])

  return (
    <HeaderContainer>
      <TitleContainer>
        {!!icon && <span style={{ color: iconColor }}>{icon}</span>}
        {!isEditing && <Text>{title}</Text>}
        <Input
          style={{ display: isEditing ? 'inline' : 'none' }}
          ref={inputFieldRef}
          value={title}
          onBlur={() => setIsEditing(false)}
          onChange={(e) => setTitle(e.target.value)}
          onPressEnter={() => setIsEditing(false)}
        />
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorText: CantileverTheme.colors.grayscale[400],
              },
            },
          }}>
          {!isPresentationModeEnabled && (
            <Button
              shape='circle'
              size='small'
              type='text'
              onClick={() => setIsEditing(true)}>
              <EditOutlined style={{ opacity: 0.4 }} />
            </Button>
          )}
        </ConfigProvider>
      </TitleContainer>
      {!isPresentationModeEnabled && (
        <Button
          shape='circle'
          size='small'
          type='text'
          onClick={toggleIsPinned}>
          {isPinned ? (
            <PushpinFilled
              style={{
                color: CantileverTheme.colors.highVis,
                opacity: 1,
              }}
            />
          ) : (
            <PushpinOutlined
              style={{
                opacity: 0.6,
              }}
            />
          )}
        </Button>
      )}
    </HeaderContainer>
  )
}

export const AbstractDrilldownPanel: FC<
  DrilldownPanelProps &
    DrilldownPanelTitleProps & {
      configPanel?: ReactNode
      children: ReactNode
    }
> = ({ children, onClose, icon, configPanel, panelPadding, panelType }) => {
  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)

  const cardRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const { height: drawerHeight } = useResizeDetector({
    targetRef: drawerRef,
  })
  useEffect(() => {
    setTimeout(
      () => cardRef.current?.scrollIntoView({ behavior: 'smooth' }),
      50
    )
  }, [])
  const [isConfigPanelOpen, setConfigPanelOpen] = useState<boolean>(false)

  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (cardRef.current) {
        cardRef.current.requestFullscreen().catch((err) => {
          console.error(
            `Error attempting to enable full-screen mode: ${err.message} (${err.name})`
          )
        })
      }
    } else {
      document.exitFullscreen()
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            bodyPadding: panelPadding ?? DEFAULT_CARD_PADDING,
          },
        },
      }}>
      <Card
        style={CARD_STYLE}
        ref={cardRef}
        title={<DrilldownPanelTitle icon={icon} panelType={panelType} />}
        extra={
          <>
            {!!configPanel && !isPresentationModeEnabled && (
              <Button
                type={isConfigPanelOpen ? 'primary' : 'text'}
                icon={<SettingOutlined />}
                onClick={() => setConfigPanelOpen((prev) => !prev)}
              />
            )}
            <Button
              type={isFullscreen ? 'primary' : 'text'}
              icon={
                isFullscreen ? (
                  <FullscreenExitOutlined />
                ) : (
                  <FullscreenOutlined />
                )
              }
              onClick={toggleFullscreen}></Button>
            {!!onClose && !isPresentationModeEnabled && (
              <Button type='text' icon={<CloseOutlined />} onClick={onClose} />
            )}
          </>
        }>
        <div
          style={{
            position: 'relative',
            height: drawerHeight ? drawerHeight + 75 : undefined,
          }}>
          {!isConfigPanelOpen && children}
          {isConfigPanelOpen && (
            <ConfigDrawerContainer>
              <Drawer
                style={{ position: 'relative' }}
                styles={{
                  wrapper: {
                    height: '100%',
                  },
                  content: {
                    height: '100%',
                  },
                  body: {
                    height: '100%',
                  },
                }}
                getContainer={false}
                title={null}
                placement='top'
                closable={false}
                open={isConfigPanelOpen}
                onClose={() => setConfigPanelOpen(false)}>
                <div ref={drawerRef}>{configPanel}</div>
              </Drawer>
            </ConfigDrawerContainer>
          )}
        </div>
      </Card>
    </ConfigProvider>
  )
}

const RawDrilldownPanel: FC<
  DrilldownPanelProps & { panelType: DrilldownPanelType }
> = ({ panelType, ...props }) => {
  const panelSpec = DRILLDOWN_PANEL_CONFIGS[panelType]

  if (!panelSpec || !panelSpec.render) {
    throw new Error(`Unknown panel type: ${panelType}`)
  }

  const childProps = useMemo(
    () => ({ ...props, icon: panelSpec.icon, panelType }),
    [panelSpec.icon, props, panelType]
  )
  return panelSpec.render(childProps)
}

export const DrilldownPanel = React.memo(RawDrilldownPanel)
